import { useMemo } from 'react'
import { PAYMENT_INFORMATION_FORM_VALUES } from 'texts/profileDetails'
import { getValidationRules } from 'utils/getValidationRules'
import { paymentInformationFormValuesSetting as formValuesSetting } from './constants'
import { FormItem, Input, Select } from '@frontend/design_system'
import { PaymentInformationFormProps } from './types'
import styles from './styles.module.scss'
import { FormItemChildren, FormValuesSetting } from 'types/form'
import { useAppSelector } from 'redux/hooks'
import {
  isMigratedUserFlowSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { REGISTRATION_STATUSES } from 'api/auth/constants'

export const PaymentInformationForm = ({
  role,
  currencies,
  readOnly,
}: PaymentInformationFormProps) => {
  const { sectionsStatuses } = useAppSelector(userDataSelector)
  const isMigratedUserFlow = useAppSelector(isMigratedUserFlowSelector)
  const isProfileIncomplete =
    sectionsStatuses?.profileStatus === REGISTRATION_STATUSES.INCOMPLETE ||
    sectionsStatuses?.profileStatus === REGISTRATION_STATUSES.REQUESTED

  const selectValues = useMemo(
    () =>
      currencies.items.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [currencies]
  )

  const validationRules = (field: FormValuesSetting['key']) =>
    useMemo(
      () => (isMigratedUserFlow ? [] : getValidationRules(field, role)),
      [role]
    )

  const isDisabledField = (name: string) =>
    readOnly ||
    isMigratedUserFlow ||
    (!isProfileIncomplete && formValuesSetting[name].disabled[role])

  return (
    <>
      <div className={styles['form-items-row']}>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.LEGAL_NAME.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.legalName)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                PAYMENT_INFORMATION_FORM_VALUES.LEGAL_NAME.PLACEHOLDER
              }
              size="large"
              error={error}
              spaceForError="auto"
              maxLength={300}
              dataTestId="payment-information-form-legal-name"
              onChange={onChange}
              label={PAYMENT_INFORMATION_FORM_VALUES.LEGAL_NAME.LABEL}
              readonly={isDisabledField('legalName')}
              isDisabled={isDisabledField('legalName')}
              isRequired={formValuesSetting.legalName.required[role]}
            />
          )}
        </FormItem>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.CURRENCY.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.currency)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Select
              size="large"
              error={error}
              onChange={onChange}
              tooltip={PAYMENT_INFORMATION_FORM_VALUES.CURRENCY.TOOLTIP}
              label={PAYMENT_INFORMATION_FORM_VALUES.CURRENCY.LABEL}
              items={selectValues}
              dataTestId="payment-information-form-currency"
              selectedValue={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.CURRENCY.PLACEHOLDER}
              isRequired
              disabled={isDisabledField('currency')}
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.REGISTRATION_NUMBER.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.registrationNumber)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                PAYMENT_INFORMATION_FORM_VALUES.REGISTRATION_NUMBER.PLACEHOLDER
              }
              size="large"
              error={error}
              maxLength={50}
              spaceForError="auto"
              onChange={onChange}
              readonly={isDisabledField('registrationNumber')}
              isDisabled={isDisabledField('registrationNumber')}
              label={PAYMENT_INFORMATION_FORM_VALUES.REGISTRATION_NUMBER.LABEL}
              isRequired={formValuesSetting.registrationNumber.required[role]}
            />
          )}
        </FormItem>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.VAT.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.vat)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.VAT.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              dataTestId="payment-information-form-vat"
              maxLength={30}
              onChange={onChange}
              readonly={isDisabledField('vat')}
              isDisabled={isDisabledField('vat')}
              label={PAYMENT_INFORMATION_FORM_VALUES.VAT.LABEL}
              isRequired={formValuesSetting.vat.required[role]}
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.BANK_NAME.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.bankName)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                PAYMENT_INFORMATION_FORM_VALUES.BANK_NAME.PLACEHOLDER
              }
              size="large"
              error={error}
              spaceForError="auto"
              maxLength={100}
              dataTestId="payment-information-form-bank-name"
              onChange={onChange}
              readonly={isDisabledField('bankName')}
              isDisabled={isDisabledField('bankName')}
              label={PAYMENT_INFORMATION_FORM_VALUES.BANK_NAME.LABEL}
            />
          )}
        </FormItem>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.IBAN.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.iban)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.IBAN.PLACEHOLDER}
              size="large"
              error={error}
              spaceForError="auto"
              maxLength={34}
              onChange={onChange}
              dataTestId="payment-information-form-iban"
              readonly={isDisabledField('iban')}
              isDisabled={isDisabledField('iban')}
              label={PAYMENT_INFORMATION_FORM_VALUES.IBAN.LABEL}
              isRequired={formValuesSetting.iban.required[role]}
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.SWIFT.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.swift)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.SWIFT.PLACEHOLDER}
              size="large"
              error={error}
              maxLength={15}
              spaceForError="auto"
              dataTestId="payment-information-form-swift"
              onChange={onChange}
              readonly={isDisabledField('swift')}
              isDisabled={isDisabledField('swift')}
              label={PAYMENT_INFORMATION_FORM_VALUES.SWIFT.LABEL}
              isRequired={formValuesSetting.swift.required[role]}
            />
          )}
        </FormItem>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.BANK_ADDRESS.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.bankAddress)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                PAYMENT_INFORMATION_FORM_VALUES.BANK_ADDRESS.PLACEHOLDER
              }
              size="large"
              error={error}
              spaceForError="auto"
              maxLength={200}
              onChange={onChange}
              dataTestId="payment-information-form-bank-address"
              readonly={isDisabledField('bankAddress')}
              isDisabled={isDisabledField('bankAddress')}
              label={PAYMENT_INFORMATION_FORM_VALUES.BANK_ADDRESS.LABEL}
            />
          )}
        </FormItem>
      </div>
      <div className={styles['form-items-row']}>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.ABA.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.aba)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.ABA.PLACEHOLDER}
              size="large"
              error={error}
              maxLength={9}
              spaceForError="auto"
              onChange={onChange}
              readonly={isDisabledField('aba')}
              isDisabled={isDisabledField('aba')}
              label={PAYMENT_INFORMATION_FORM_VALUES.ABA.LABEL}
            />
          )}
        </FormItem>
        <FormItem
          id={PAYMENT_INFORMATION_FORM_VALUES.EMAIL.ID}
          className={styles['form-item']}
          rules={validationRules(formValuesSetting.accountingEmail)}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={PAYMENT_INFORMATION_FORM_VALUES.EMAIL.PLACEHOLDER}
              size="large"
              error={error}
              maxLength={254}
              spaceForError="auto"
              onChange={onChange}
              readonly={isDisabledField('accountingEmail')}
              isDisabled={isDisabledField('accountingEmail')}
              label={PAYMENT_INFORMATION_FORM_VALUES.EMAIL.LABEL}
            />
          )}
        </FormItem>
      </div>
    </>
  )
}
